/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/virtual/virtual_pv_template_helpers.test.ts
 */

import {
	getPVInitData,
	toPercent,
} from 'src/components/Consumption/PVSetupEnergy/pv_setup_live_calculate_helper';
import { getBatteryIconStatus } from '../virtual_icons_helpers';
import { convertToUnit } from 'src/helpers/units_helpers';
//Types
import type { PVLiveData } from 'src/components/Consumption/PVSetupEnergy/pv_setup_live_calculate_helper';
import type { EnergyDistributionPropsTypes } from '@Shelly/shelly-design-system/index';
import type { VCInterfaceStatus } from 'src/types/VirtualComponents';

type Role =
	| 'house_consumption'
	| 'grid_consumption'
	| 'pv_consumption'
	| 'battery_consumption'
	| 'battery_level'
	| 'battery_state';

const byRole = (statuses: VCInterfaceStatus[], role: Role) => {
	return statuses.find((s) => s?.role === role);
};

export const buildPvTemplateCard = (statuses: VCInterfaceStatus[]) => {
	const houseConsumption = byRole(statuses, 'house_consumption');
	const gridConsumption = byRole(statuses, 'grid_consumption');
	const pvConsumption = byRole(statuses, 'pv_consumption');
	const batteryConsumption = byRole(statuses, 'battery_consumption');
	const batteryLevel = byRole(statuses, 'battery_level');
	const batteryStatus = byRole(statuses, 'battery_state');

	const data: PVLiveData = calculatePVFlow(
		gridConsumption,
		pvConsumption,
		houseConsumption,
		batteryConsumption,
		batteryLevel,
		getPVInitData(),
	);
	data.batteryStatus = batteryStatus?.value;
	data.batteryStatusIcon = batteryStatus?.icon;
	data.homeProgress = [
		{
			id: '1',
			percent: toPercent(data.houseConsumption, data.gridPower),
			color: 'grid',
		},
		{
			id: '2',
			percent: toPercent(data.houseConsumption, data.solarPower),
			color: 'solar',
		},
		{
			id: '3',
			percent: toPercent(data.houseConsumption, data.batteryPower),
			color: 'battery',
		},
	];
	return {
		...data,
		cards: getCards(data, batteryConsumption != null),
		paths: getPaths(data),
	};
};

const calculatePVFlow = (
	grid: VCInterfaceStatus,
	pv: VCInterfaceStatus,
	house: VCInterfaceStatus,
	batteryConsumption: VCInterfaceStatus,
	batterySoc: VCInterfaceStatus,
	data: PVLiveData,
) => {
	const gridPower = grid?.event ?? 0; // +import / -export
	const pvPower = pv?.event ?? 0; // PV generation (>= 0)
	const houseConsumption = house?.event ?? 0;
	const batteryPower = batteryConsumption?.event ?? 0; // +discharge / -charge
	const batteryLevel = batterySoc?.event ?? 0;

	const gridImport = gridPower > 0 ? gridPower : 0;
	const gridExport = gridPower < 0 ? Math.abs(gridPower) : 0;
	const battDischarge = batteryPower > 0 ? batteryPower : 0;
	const battCharge = batteryPower < 0 ? Math.abs(batteryPower) : 0;
	const pvSelfPower =
		houseConsumption + battCharge + gridExport - (pvPower + gridImport + battDischarge);

	data.gridPower = gridPower;
	data.solarPower = Math.max(0, pvPower + pvSelfPower);
	data.houseConsumption = houseConsumption;
	data.batteryPower = batteryPower;
	data.batteryLevel = batterySoc ? batteryLevel : null;
	data.pvSelfPower = pvSelfPower;

	data.gridToHome = gridPower > 0 && houseConsumption > 0;
	data.solarToHome = pvPower > 0 && houseConsumption - gridImport - battDischarge > 0;
	data.batteryToHome = batteryPower > 0 && houseConsumption > 0;
	data.solarToGrid = gridPower < 0 && pvPower > 0;
	data.batteryToGrid = gridPower < 0 && batteryPower > 0;

	data.solarToBattery = batteryPower < 0 && pvPower > 0;
	data.gridToBattery = batteryPower < 0 && pvPower <= 0 && gridPower > 0;
	data.solarToGridPower =
		data.solarToGrid && gridPower < 0
			? Math.max(0, Math.abs(gridPower) - (data.batteryToGrid ? batteryPower : 0))
			: 0;

	return data;
};

const getCards = (data: PVLiveData, showBattery: boolean) => {
	const unit = 'W';
	const cards = [
		{
			label: 'Home',
			icon: 'fas fa-home',
			progress: data.homeProgress,
			value1: { text: convertToUnit(data.houseConsumption, unit), icon: 'fa fa-bolt' },
			tooltip: [],
		},
		{
			label: 'Grid',
			icon: 'fas fa-utility-pole text-grid',
			progress: [{ id: '1', percent: 100, color: data.solarToGrid ? 'solar' : 'grid' }],
			value1: { text: convertToUnit(data.gridPower, unit), icon: 'far fa-arrow-right-to-line' },
			value2: data.solarToGrid
				? { text: convertToUnit(data.solarToGridPower, unit), icon: 'far fa-arrow-left-to-line' }
				: undefined,
			tooltip: [],
		},
		{
			label: 'Solar',
			icon: 'fas fa-solar-panel text-solar',
			progress: [{ id: '2', percent: 100, color: 'solar' }],
			value1: { text: convertToUnit(Math.abs(data.solarPower), unit), icon: 'fa fa-bolt' },
			value2:
				data.pvSelfPower < 0
					? { text: convertToUnit(Math.abs(data.pvSelfPower), unit), icon: 'fa fa-fan' }
					: undefined,
			tooltip: [],
		},
	] as EnergyDistributionPropsTypes['cards'];
	if (showBattery) {
		cards.push({
			label: 'Battery',
			icon: 'fas fa-car-battery text-battery',
			progress: [{ id: '3', percent: 100, color: 'battery' }],
			value1: { text: convertToUnit(data.batteryPower, unit), icon: 'fa fa-bolt' },
			value2:
				data.batteryLevel != null
					? {
							text: `${data.batteryLevel} %`,
							icon: getBatteryIconStatus(data.batteryLevel).icon,
							// icon: 'fa fa-battery-full',
						}
					: undefined,
			tooltip: [],
		});
	}
	return cards;
};

const getPaths = (data) => {
	return {
		leftToRight: {
			isActive: data.solarToGrid || data.gridToSolar,
			isReverse: data.gridToSolar,
			animationSeconds: 3,
		},
		leftToCenter: {
			isActive: data.gridToHome,
			animationSeconds: 3,
		},
		rightToCenter: {
			isActive: data.solarToHome,
			animationSeconds: 3,
		},
		leftToTopRight: {
			isActive: data.gridToBattery || data.batteryToGrid,
			isReverse: data.batteryToGrid,
			animationSeconds: 3,
		},
		topRightToCenter: {
			isActive: data.batteryToHome,
			animationSeconds: 3,
		},
		botToTop: {
			isActive: data.solarToBattery,
			animationSeconds: 3,
		},
	};
};
